import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

/**
  This utility is a thin wrapper around the underlying React.context tool.
  It adds the ability to handle a missing provider in whatever way you see fit.

  You must provide the `fallbackCallback` function, and it will be invoked
  when no provider exists in ancestry. You can throw an error or return a
  default value, or anything in between.

  It returns {Provider, Consumer, useContext}, which are the only three tools
  you need when working with context. Note that these are not type-compatible
  with OG React context!

  The Consumer and useContext hook behave as you would expect. In addition to
  the normal context consumption, they use the fallbackCallback when necessary.

  Our custom Provider is a bit more of a departure from the React context provider:

    The normal Provider suffers from important performance problems. Our custom provider
    blocks unnecessary re-renders when the context value does not change. (Details below.)

    The test for "did the context value change?" uses `react-fast-compare`, unless
    you provide your own comparison function on the second parameter.

  There is also the ability to specify a validation function which will be run on the Provider's value
  whenever that changes. The validation function must be side-effecting meaning as it's just run on the value
  and doesn't change behavior. This allows the user to either log or throw an assertion error if the
  validation fails.
*/

export interface CustomContextPackage<ContextType> {
  Consumer: (props: {children: (context: ContextType) => JSX.Element | null}) => JSX.Element;
  Provider: ({value, children}: {value: ContextType; children: ReactNode}) => JSX.Element;
  useContext: () => ContextType;
}

export function makeReactContext<ContextType>(
  fallbackCallback: () => ContextType,
  displayName: string,
  isEqualFn: (a: ContextType, b: ContextType) => boolean = isEqual,
  opts?: {validationFn: (args: ContextType) => void}
): CustomContextPackage<ContextType> {
  // We use a magic object as the initial value so we can trap it below. No consumer
  // will ever actually receive it as context.
  const missingContextSentinel = {};
  const rawContext = createContext<ContextType>(missingContextSentinel as ContextType);
  rawContext.displayName = displayName;
  const {Consumer: RawConsumer, Provider: RawProvider} = rawContext;

  type ConsumerCallback = (props: {children: (context: ContextType) => JSX.Element | null}) => JSX.Element;

  const SmartConsumer: ConsumerCallback = ({children}) => (
    <RawConsumer>
      {(context) => {
        if (context === missingContextSentinel) {
          context = fallbackCallback();
        }
        return children(context);
      }}
    </RawConsumer>
  );
  // @ts-expect-error
  SmartConsumer.displayName = `${displayName}.SmartConsumer`;

  // This Provider ensures downstream consumers do not re-render unless the context value has changed.
  // The basic technique of moving context into state is described in the react docs:
  //   https://reactjs.org/docs/context.html#caveats
  //
  function MemoProvider({value, children}: {value: ContextType; children: ReactNode}): JSX.Element {
    // Track the current value in state
    const [stateValue, setStateValue] = useState(value);

    // Use an effect to ensure that when the `value` prop changes, we re-render appropriately.
    // (Without this, consumers will have stale values, and not re-render when they should.)
    useEffect(() => {
      // Only update the state value if the `value` prop has actually changed. The equality check
      // defaults to a deep-equals check, but you can give us a more intelligent function. This guards
      // against the common scenario of passing `value={{my: object}}`, which fails shallow equality
      // and will re-render consumers too aggressively.
      //
      // Note that the React team doesn't recommend using deep equality checks in effect hooks:
      //  https://github.com/facebook/react/issues/14476#issuecomment-471199055
      //
      // For small objects, or rarely-changing objects, this is fine. Or, as mentioned above,
      // feel free to memoize context values that you give to <Provider>
      //
      if (!isEqualFn(value, stateValue)) {
        setStateValue(value);
      }
    }, [stateValue, value]);

    if (opts?.validationFn) {
      opts.validationFn(value);
    }

    return <RawProvider value={stateValue}>{children}</RawProvider>;
  }

  function useCustomContext(): ContextType {
    const value = useContext(rawContext);
    return value === missingContextSentinel ? fallbackCallback() : value;
  }

  return {
    Consumer: SmartConsumer,
    Provider: MemoProvider,
    useContext: useCustomContext,
  };
}
